<template>
  <div class="position-relative">
    <div class="mt-4">
      <div v-for="group in groupedDomainEvents" :key="group.entityId || '__unlinked__'" class="mb-8">
        <div class="d-flex align-center mb-3">
          <v-icon class="mr-2" size="small">mdi-database</v-icon>
          <span class="text-subtitle-1 font-weight-medium">{{ group.entityName }}</span>
          <v-chip density="compact" class="ml-2" size="small">{{ group.domainEvents.length }}</v-chip>
        </div>
        <div class="d-flex flex-wrap ga-4 align-start">
          <domain-event-card
            v-for="domainEvent in group.domainEvents"
            :key="domainEvent.id"
            :schema="domainEvent"
            :usageStatus="domainEventUsageStatus.get(domainEvent.id)"
            :deleting="deletingId === domainEvent.id"
            @deleteSchema="handleDeleteDomainEvent"
          />
          <div
            class="add-card-placeholder d-flex align-center justify-center"
            :style="{ borderColor: cardColor }"
            :class="{ 'add-card-placeholder--disabled': store.state.isWorkflowDisabled || addingEntityId === group.entityId }"
            @click="!store.state.isWorkflowDisabled && addDomainEvent(group.entityId)"
          >
            <v-progress-circular v-if="addingEntityId === group.entityId" :size="24" :width="2" indeterminate :color="cardColor" />
            <v-icon v-else :color="cardColor" size="28">mdi-plus</v-icon>
          </div>
        </div>
      </div>
    </div>

    <delete-confirmation-dialog
      v-model="openDeleteDialog"
      :title="dialogInfo.title"
      :text="dialogInfo.text"
      :onDelete="dialogInfo.action"
    />
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import DomainEventCard from '@/components/DomainEventCard.vue'
import DeleteConfirmationDialog from '@/components/DeleteConfirmationDialog.vue'
import workflowApi from '@/api/workflowApi'
import useDomainEventActions from '@/composables/useDomainEventActions'
import useDomainModel from '@/composables/useDomainModel'
import { diagramHelpers } from '@/mixins/diagramHelpers'
import { SchemaType } from '@/constants/schema'
import { DomainModelRole } from '@/constants/cardTypes'

const { cardTypeColor } = useDomainModel()
const { deleteDomainEvent } = useDomainEventActions()

const store = useStore()
const cardTypes = computed(() => store.state.cardTypes.cardTypes)
const domainEventCardType = computed(() => diagramHelpers.methods.getDomainEventCardType(cardTypes.value))
const cardColor = computed(() => cardTypeColor(domainEventCardType.value) || '#eeeeee')
const workflow = computed(() => store.state.workflow.workflow)
const domainEvents = computed(() => (workflow.value?.schemas ?? []).filter((schema) => schema.type === SchemaType.DOMAIN_EVENT))
const entities = computed(() => (workflow.value?.schemas ?? []).filter((schema) => schema.type === SchemaType.ENTITY))

const domainEventUsageStatus = computed(() => {
  const usedIds = new Set()

  const events = workflow.value?.domainEvents ?? []
  events.forEach((event) => {
    event.cards?.forEach((card) => {
      if (card.schemaId && card.cardType?.domainModelRole === DomainModelRole.DOMAIN_EVENT) {
        usedIds.add(card.schemaId)
      }
    })
  })

  const schemas = workflow.value?.schemas ?? []
  const readModels = schemas.filter((s) => s.type === SchemaType.QUERY)

  readModels.forEach((readModel) => {
    if (Array.isArray(readModel.domainEventIds)) {
      readModel.domainEventIds.forEach((id) => usedIds.add(id))
    }
  })

  const statusMap = new Map()
  domainEvents.value.forEach((de) => {
    if (!usedIds.has(de.id)) {
      statusMap.set(de.id, 'unused')
    }
  })

  return statusMap
})

const groupedDomainEvents = computed(() => {
  const entityMap = new Map()
  const unlinked = []

  domainEvents.value.forEach((de) => {
    if (de.entityId) {
      if (!entityMap.has(de.entityId)) {
        entityMap.set(de.entityId, [])
      }
      entityMap.get(de.entityId).push(de)
    } else {
      unlinked.push(de)
    }
  })

  const groups = []

  entityMap.forEach((des, entityId) => {
    const entity = entities.value.find((e) => e.id === entityId)
    groups.push({
      entityId,
      entityName: entity?.name || 'Unknown Entity',
      domainEvents: des
    })
  })

  groups.sort((a, b) => a.entityName.localeCompare(b.entityName))

  groups.push({
    entityId: null,
    entityName: 'Not linked to entity',
    domainEvents: unlinked
  })

  return groups
})

const addingEntityId = ref(undefined)
const deletingId = ref(null)
const openDeleteDialog = ref(false)
const dialogInfo = ref({ title: '', text: '', action: null })

const addDomainEvent = async (entityId) => {
  addingEntityId.value = entityId
  try {
    const existingNames = new Set(
      domainEvents.value.map((s) => s.name.toLowerCase())
    )

    const counter = Array.from({ length: domainEvents.value.length + 1 }, (_, i) => i + 1)
      .find((n) => !existingNames.has(`new domain event ${n}`))

    const candidateName = `New Domain Event ${counter}`

    const newSchema = {
      name: candidateName,
      type: SchemaType.DOMAIN_EVENT,
      fields: []
    }

    if (entityId) {
      newSchema.entityId = entityId
    }

    const newSchemas = [...(workflow.value.schemas || []), newSchema]

    await workflowApi.updateSchemas({
      schemas: newSchemas.length > 0 ? newSchemas : undefined,
      workflow: workflow.value
    })
  } finally {
    addingEntityId.value = undefined
  }
}

const getDomainEventUsageCount = (schemaId) => {
  const events = workflow.value?.domainEvents ?? []
  let count = 0
  events.forEach((event) => {
    event.cards?.forEach((card) => {
      if (card.schemaId === schemaId) {
        count++
      }
    })
  })

  return count
}

const handleDeleteDomainEvent = async (schemaId) => {
  const usageCount = getDomainEventUsageCount(schemaId)

  if (usageCount === 0) {
    deletingId.value = schemaId
    try {
      await deleteDomainEvent(schemaId)
    } finally {
      deletingId.value = null
    }

    return
  }

  openDeleteDialog.value = true
  dialogInfo.value = {
    title: 'Confirm domain event deletion',
    text: `This domain event is currently used by ${usageCount} card${usageCount > 1 ? 's' : ''} in the domain model. Deleting it will unlink those cards. Are you sure?`,
    action: () => deleteDomainEvent(schemaId)
  }
}
</script>

<style scoped>
.add-card-placeholder {
  min-width: 200px;
  max-width: 300px;
  height: 92px;
  border: 1px dashed;
  border-radius: 8px;
  background: var(--light-1);
  cursor: pointer;
  transition: opacity 0.2s;
  opacity: 0.5;
}

.add-card-placeholder:hover {
  opacity: 1;
}

.add-card-placeholder--disabled {
  cursor: default;
  opacity: 0.3;
  pointer-events: none;
}
</style>
